import axios from 'axios'
import {
  clinicApiURL,
  getCountryCode,
  getCPDataWithoutParams,
  getMainLanguageCode,
} from 'src/clinic/services'
import { commonHeadersWeb } from 'src/consumer/services'

/**
 * Clinic-side patient documents (ID proof, old reports, referrals …) uploaded from the
 * patient form. One endpoint serves every staff portal; the server checks that the caller may
 * see the patient. Binary goes through the platform media pipeline (private access).
 */

export const PATIENT_DOCUMENTS_API = '/api/secure/patient-documents'
export const PATIENT_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024
export const PATIENT_DOCUMENT_ACCEPT = 'image/*,application/pdf'

export const PATIENT_DOCUMENT_CATEGORIES = [
  { value: 'ID_PROOF', label: 'ID proof' },
  { value: 'LAB_REPORT', label: 'Lab report' },
  { value: 'IMAGING', label: 'Imaging' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'OTHER', label: 'Other' },
]

function authHeaders() {
  let token = null
  try {
    token = localStorage.getItem('token')
  } catch (error) {
    token = null
  }
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...commonHeadersWeb,
    'ef-user-country-code': getCountryCode(),
    'ef-app-language': getMainLanguageCode(),
  }
}

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

/** A file picked in the form but not yet uploaded (the patient may not exist yet). */
export function stagePatientDocument(file, { category = 'OTHER' } = {}) {
  const isImage = typeof file?.type === 'string' && file.type.startsWith('image/')
  return {
    localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    name: file?.name || 'document',
    size: file?.size || 0,
    type: file?.type || '',
    category,
    previewUrl: isImage && typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : null,
    status: 'staged',
    error: null,
  }
}

export function releasePatientDocumentPreview(staged) {
  if (staged?.previewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
    try {
      URL.revokeObjectURL(staged.previewUrl)
    } catch (error) {
      // ignore
    }
  }
}

export async function uploadPatientDocument({ patientId, file, category, description }) {
  const formData = new FormData()
  formData.append('file', file, file?.name || 'document')
  formData.append('patientId', patientId)
  if (category) formData.append('category', category)
  if (description) formData.append('description', description)
  const response = await axios.post(`${clinicApiURL}${PATIENT_DOCUMENTS_API}/upload`, formData, {
    headers: authHeaders(),
  })
  return response?.data
}

/** Uploads every staged file for a freshly created patient; never throws, reports per file. */
export async function uploadStagedPatientDocuments(patientId, stagedDocuments = []) {
  const results = []
  for (const staged of stagedDocuments) {
    if (!staged?.file) continue
    try {
      const uploaded = await uploadPatientDocument({
        patientId,
        file: staged.file,
        category: staged.category,
        description: staged.description,
      })
      results.push({ localId: staged.localId, ok: true, document: uploaded })
    } catch (error) {
      results.push({ localId: staged.localId, ok: false, error })
    } finally {
      releasePatientDocumentPreview(staged)
    }
  }
  return results
}

export async function listPatientDocuments(patientId) {
  if (!patientId) return []
  const response = await getCPDataWithoutParams(
    `${PATIENT_DOCUMENTS_API}/list?patientId=${encodeURIComponent(patientId)}`,
  )
  if (response?.status !== 200) return []
  const items = response?.data?.items
  return Array.isArray(items) ? items : []
}

export async function deletePatientDocument(documentId) {
  const response = await axios.delete(`${clinicApiURL}${PATIENT_DOCUMENTS_API}/${encodeURIComponent(documentId)}`, {
    headers: authHeaders(),
  })
  return response?.status === 200
}

/** Opens a document in a new tab through the redirecting download endpoint. */
export function getPatientDocumentOpenUrl(document) {
  if (!document?.id) return ''
  return `${clinicApiURL}${PATIENT_DOCUMENTS_API}/${encodeURIComponent(document.id)}/download`
}
